// The main LCARS HUD frame (port of imperial-lcars examples/user1.html "main"):
// full-screen frame in game.gui (camera-locked, 1 unit = 1 px), top border with the
// always-visible sector jump form (X/Y inputs + GO → navigation.loadPage('/x/y'), the home component jumps the map), left nav.
// Content area: the routed page (left, built by the canvas renderer in client.js) and the
// Messages/Notifications overlays (right) — hud-panels.js; the map shows through the rest.
// Planets / Settings NAVIGATE (routed pages, user 2026-10-10); Messages / Notifications toggle overlays.
import { LcarsBox, selectable, editable, loadFontFamily, box } from 'imperial-lib';

// the brand face for the title (user 2026-10-11: "make miserit anthar homenaje")
const BRAND_FONT = 'https://cdn.worldapi.org/imperial-text-base/google/homenaje/font.json';
let brandFont = null;
// client.js starts these before the renderer and the ship models are set up (they are independent of both), so the
// font files download while the game boots instead of after it (desktop-load #58)
export function prewarmHudFonts() { ensureLcarsFont(); ensureIconFont(); ensureBrandFont(); }
function ensureBrandFont() {
	return brandFont || (brandFont = loadFontFamily(BRAND_FONT));
}
import { ensureFont, ensureLcarsFont, ensureInteractionRuntime } from './box-patcher.js';
import { createHudPanels, NOTIFICATION_COUNT } from './hud-panels.js';
import { initHudToggle, syncHudVisibility, toggleHud, ensureIconFont, ICON_VISIBILITY_OFF } from './hud-toggle.js';
import { guiSurfaceHitRay } from './gui-ray.js';
import { isPhoneLayout, ICON_SEARCH, ICON_MAIL, ICON_BELL } from './phone-layout.js';

// phone: the X/Y/GO search is hidden behind a loupe; open, it takes the whole top bar (eye stays)
let phoneSearchOpen = false;
// The X/Y form always shows the map's current sector (user 2026-10-24: "the form should reflect the same as the url"):
// the /x/y of the URL, else the sector the map is on, else 0 0.
export function currentXY() {
	const m = location.pathname.match(/^\/(-?\d+)\/(-?\d+)(?:\/|$)/);
	const sc = globalThis.game?.sectorMap?.sectorCoordinates;
	if (m) return { x: m[1], y: m[2] };
	return { x: String(Number(sc?.x) || 0), y: String(Number(sc?.y) || 0) };
}
// push the current coordinate into the existing fields (a jump, back/forward, a link; the fields were built before)
export function syncXyFields() {
	const f = globalThis.game?.lcarsHud?.fields; if (!f?.x || !f?.y) return;
	const xy = currentXY();
	for (const k of ['x', 'y']) if (f[k].opts.text !== xy[k] && !f[k].editing?.selection?.active) { f[k].opts.text = xy[k]; f[k].setText(xy[k]); }
}
// phone: the frame has three bars (top, left, bottom), each thin or expanded to the desktop version with its buttons inside
// (user 2026-10-21: "tap expands. only one of the 3 can be expanded, by default its top"); the frame is built again per state.
// Thin: top = the title only, left = the 12 px spine, bottom = a 12 px bar.
let expandedBar = 'top';
let activePageKey = null; // the page's root key ('planets-page') captured at expand: its block is blue
// every page load on the phone (user 2026-10-21/10-24, no flag): expanded, collapsed a moment later, then a tooltip until the first tap
const ICON_ARROW_BACK = '\uE5C4'; // Material Icons arrow_back (PUA)
const HINT_COLLAPSE_MS = 600;
let hintArmed = false;
let hintVisible = false;

const DEV = !!location.port; // the dev server runs on a port; production has none
const here = (path) => location.pathname === path ? '/' : path; // nav buttons toggle their page

// A frame item with `href` navigates like an anchor (the framework's navigation, no hand-wired
// handler; user 2026-10-11: "we have no href?"). `toggle: true` = pressing it on its own page
// goes back to /. Items with their own onClick pass through.
function hrefItems(items) {
	return items.map((item) => {
		if (!item.href) return item;
		return { ...item, onClick: () => navigation.loadPage(item.toggle ? here(item.href) : item.href) };
	});
}

// The frame shows the session user's state (user 2026-10-10: logged out → a Login button
// top-right; after the e-mail + code login → Messages / Notifications). `me()` is an RPC: right
// after boot the socket may not be open yet → null now, rebuild once it connects.
let userRebuildArmed = false;
let nextColonizableSkips = 0; // the 'Next free planet' button walks through the free planets
async function fetchUser() {
	let r = null;
	try { r = await provider.request('game', 'session.js', 'me'); } catch (e) { r = null; }
	if (r && r.$offline) {
		if (!userRebuildArmed) { userRebuildArmed = true; observer.connect('webSocketClient.reconnect', () => { userRebuildArmed = false; updateLcarsHud({ refreshUser: true }); }); }
		return null;
	}
	return r && r._id ? r : null;
}

// build the frame again (VR relayout, login/logout, the phone's bars). The OLD frame, panels and phone bars stay up
// while the new ones are built (fonts, the session RPC) and are swapped out in ONE synchronous step right before the
// new frame is added (`retireOld`): the HUD is never missing for a frame (user 2026-10-21: "it completely replaces the
// ui on tap … making it disappear for a second").
let retiring = null;
// PARTIAL REDRAW (user 2026-10-21): a change of the frame's content — the phone's bars, login/logout, the loupe — goes
// through LcarsBox#update (imperial-lcars): the frame object, its mesh and every unchanged text / click proxy stay, only
// what changed is created; no frame is ever missing and nothing is rebuilt. The VR relayout (new viewport) and the socket
// reconnect keep the full rebuild above (the swap stopgap `retireOld` exists only for that path).
export function updateLcarsHud({ refreshUser = false } = {}) {
	// decided INSIDE the queue: a build that is still in flight (the first one) has set game.lcarsHud by then
	return queueHudWork(() => (!game.lcarsHud || typeof game.lcarsHud.update !== 'function') ? rebuildNow() : buildHud({ partial: true, refreshUser }));
}
export function rebuildLcarsHud() {
	return queueHudWork(rebuildNow);
}
function rebuildNow() {
	const gui = game.gui;
	retiring = {
		spine: game.phoneSpine,
		panels: game.hudPanels,
		frames: gui.children.filter(c => typeof c._build === 'function' && typeof c.dispose === 'function'),
	};
	return buildHud();
}
// ONE HUD build at a time (#61): the first build awaits fonts and the session RPC; a socket reconnect, a relayout or a login
// that arrives meanwhile used to start a second FULL build next to it (two frames; the login only updated one, the other
// kept its old "LOGIN" / "Settings" drawn under the new text). Every build, update and rebuild now waits for the one before.
let hudQueue = Promise.resolve();
function queueHudWork(fn) {
	const p = hudQueue.then(fn);
	hudQueue = p.catch(() => {}); // a failed build does not block the next one
	return p;
}
function retireOld() {
	if (!retiring) return;
	const { spine, panels, frames } = retiring;
	retiring = null;
	spine?.dispose();
	panels?.dispose();
	for (const c of frames) c.dispose();
}

// phone: the tap target on the spine (open: on the column) and the first-entry tooltip. box-ui boxes in game.gui like
// the page and the eye (hud-toggle.js hides/shows them with the frame); rebuilt with the frame.
function createPhoneSpine(main, base, applyOrder, iconFont) {
	const parts = [];
	const ca = main.contentArea;
	const W = main.width, H = main.height;
	const tW = main.sides.top.width, bW = main.sides.bottom.width, lW = main.sides.left.width;
	// a tap on a thin bar expands it; a tap on an expanded left/bottom bar (beside its buttons) goes back to the default (top)
	const hitFor = (bar, w, h, x, y) => {
		const hit = box({ width: w, height: h, backgroundColor: [0, 0, 0, 0.01], clickable: true, onClick: () => { dismissHint({ relayout: false }); setBar(expandedBar === bar ? 'top' : bar); } });
		hit.userData.rayOpaque = true;
		hit.position.set(x, y, 0.3); // under the bars' button proxies (z 0.6)
		parts.push(hit);
	};
	if (expandedBar === 'left') hitFor('left', lW, ca.h, -W / 2 + lW / 2, ca.y);
	else hitFor('left', 30, ca.h, -W / 2 + 15, ca.y); // thin: the 12 px spine + a finger's margin over the page padding
	if (expandedBar === 'bottom') hitFor('bottom', W, bW, 0, -H / 2 + bW / 2);
	else hitFor('bottom', W, 30, 0, -H / 2 + 15);
	if (expandedBar !== 'top') hitFor('top', W - 160, tW + 8, 80, H / 2 - tW / 2 - 4); // thin top: right of the title
	// first-entry tooltip (ticket #3): an LCARS pill in the frame's own colour with dark text, under the top bar beside the
	// spine. It lives in a Group of its own with a HIGH renderOrder (three sorts by the nearest Group's number first, like the
	// keyboard holder in box-patcher.js): as a bare child of game.gui a routed page's panel painted over it (measured on the Moto
	// at /planets: 0 text pixels visible) — only the bare / entry showed it.
	let tip = null, tipIcon = null, tipHolder = null;
	if (hintVisible) {
		const c = main.color || [1, 1, 1, 1];
		// one pill in two parts (a box carries one font): an icon segment (Material Icons arrow_back) + the text segment, no gap,
		// same height / colour / opacity; left edge as before (spine + 8)
		const bgc = { backgroundColor: [c[0], c[1], c[2]], backgroundOpacity: c[3] ?? 1 };
		const ICON_W = 32, TEXT_W = 190, left = -W / 2 + 12 + 8, y = H / 2 - tW - 8 - 15;
		tipIcon = box({ width: ICON_W, height: 30, text: ICON_ARROW_BACK, font: iconFont, fontSize: 22, color: [0, 0, 0], textAlign: 'center', vAlign: 'middle', padding: [0, 0, 0, 0], // icon font: imperial-text-base box-text centres its ink box on the box centre (no hand-nudge; 2026-10-23, ticket #19)
			...bgc, borderRadii: [15, 0, 0, 15] });
		tipIcon.position.set(left + ICON_W / 2, y, 0.3);
		tip = box({ width: TEXT_W, height: 30, text: 'TAP THE SPINE TO EXPAND MENU', fontSize: 16, color: [0, 0, 0], textAlign: 'center', vAlign: 'middle', padding: [0, 0, 0, 0],
			...bgc, borderRadii: [0, 15, 15, 0] });
		tip.position.set(left + ICON_W + TEXT_W / 2, y, 0.3);
		tipHolder = new (game.gui.constructor)();
		tipHolder.renderOrder = (game.gui.renderOrder || 10000) + 5000;
		tipHolder.userData.layoutIgnore = true;
		game.gui.add(tipHolder);
		tipHolder.add(tipIcon, tip);
		for (const b of [tipIcon, tip]) { b.updateRenderOrder?.(tipHolder.renderOrder); b.traverse((o) => { if (o.material) o.material.depthTest = false; }); }
		tip.userData.hintShownAt = performance.now();
		game.phoneHintShownAt = tip.userData.hintShownAt; // read by the tooltip probes
	}
	for (const p of parts) { game.gui.add(p); applyOrder(p); p.renderOrder = base + 60; p.updateRenderOrder?.(base + 60); }
	const dropTip = () => { if (tip) { tipIcon?.dispose?.(); tip.dispose?.(); tip.parent?.remove(tip); tipHolder?.parent?.remove(tipHolder); tip = null; tipHolder = null; } };
	return { dropTip, dispose() { dropTip(); for (const p of parts) { p.dispose?.(); p.parent?.remove(p); } } };
}

// one bar expanded at a time; the mounted page's block is blue in the bar
function setBar(bar) {
	expandedBar = bar;
	phoneSearchOpen = false;
	activePageKey = game.hudPanels?.page()?.__uiRootView?.key ?? null;
	return updateLcarsHud();
}

// the page slot starts below the tooltip pill while it is shown (ticket #33: a directly loaded page's title bar was under it):
// pill bottom + a gap, measured from the frame's own geometry (the pill is placed in createPhoneSpine)
const HINT_GAP = 8, HINT_H = 30;
function hintInset(main, pad = 16) {
	if (!hintVisible || !game.phoneLayout) return 0;
	const ca = main.contentArea;
	const pillBottom = main.height / 2 - main.sides.top.width - 8 - HINT_H;
	const pageTop = ca.y + ca.h / 2 - pad;
	return Math.max(0, Math.ceil(pageTop - pillBottom + HINT_GAP));
}

// relayout: the page slot takes its normal place again (a tap on a spine hit area follows with its own setBar, no second update)
function dismissHint({ relayout = true } = {}) {
	if (!hintVisible) return;
	hintVisible = false;
	game.phoneSpine?.dropTip();
	if (relayout) updateLcarsHud();
}

// every load: as soon as the frame is up (and visible): the left bar expanded, 600 ms later back to the top bar + the tooltip
function armHint() {
	if (hintArmed) return;
	hintArmed = true;
	const poll = setInterval(async () => {
		if (game.hud?.hidden || !game.lcarsHud) return; // the user hid it with the eye: waits until it is shown
		clearInterval(poll);
		await setBar('left');
		setTimeout(async () => {
			hintVisible = true;
			await setBar('top');
			// the first touch hides the pill; once the press is over (never while the finger is down: the press reaches its
			// target where it went down) the page slot takes its place, whether the press began on the map or on the HUD.
			// The panels keep their state across the relayout (hud-panels: typed login text, open overlay, late replies).
			window.addEventListener('pointerdown', (e) => {
				if (!hintVisible) return;
				hintVisible = false;
				game.phoneSpine?.dropTip();
				const done = () => {
					window.removeEventListener('pointerup', done, true); window.removeEventListener('pointercancel', done, true);
					setTimeout(() => updateLcarsHud(), 0);
				};
				window.addEventListener('pointerup', done, true); window.addEventListener('pointercancel', done, true);
			}, { capture: true, once: true });
		}, HINT_COLLAPSE_MS);
	}, 120);
}

// The LCARS face is Antonio (imperial-lcars examples/user1.html; box-patcher's ensureLcarsFont,
// shared with `$type: 'lcars'` page views); the frame had been built with the dialogs' DejaVu
// Sans via box-patcher's ensureFont (user 2026-10-10: "wrong font").

// The DOM badge `client-build-hash` (instances.js) is a fixed element over the canvas. Put it inside the frame's content area at its bottom-left
// corner (main.contentArea = centre x/y/w/h in GUI px, origin at the viewport centre, 1 unit = 1 css px in 2D), so it never covers a bar or its
// labels; the area changes with the phone's expanded bars, so every frame build/update calls this. pointer-events stay none (CSS).
function placeBuildBadge(main) {
	const badge = document.querySelector('client-build-hash'); const ca = main?.contentArea;
	if (!badge || !ca || globalThis.game?.renderer?.xr?.isPresenting) return;
	const W = window.innerWidth, H = window.innerHeight;
	const left = Math.max(0, W / 2 + ca.x - ca.w / 2) + 4, bottom = Math.max(0, H / 2 + ca.y - ca.h / 2) + 4;
	badge.style.left = left + 'px'; badge.style.bottom = bottom + 'px';
	badge.style.maxWidth = Math.max(60, ca.w - 8) + 'px'; badge.style.overflow = 'hidden'; badge.style.whiteSpace = 'nowrap';
}

export function initLcarsHud(opts = {}) {
	return queueHudWork(() => buildHud(opts));
}
async function buildHud({ partial = false, refreshUser = true } = {}) {
	const existing = partial ? game.lcarsHud : null;
	// all five are independent: fetched together, not one after the other (desktop-load, #58).
	// the dialogs' font (DejaVu) is NOT awaited: the interaction runtime attaches the XR keyboard when it arrives
	const [font, user, iconFont, brandFontResolved] = await Promise.all([
		ensureLcarsFont(),
		existing && !refreshUser ? game.user : fetchUser(),
		ensureIconFont(), ensureBrandFont(),
	]);
	ensureInteractionRuntime();
	game.user = user;
	// far right of the top bar: the HUD visibility toggle (user 2026-10-10); when the HUD is hidden
	// the frame is gone too, so hud-toggle.js shows a floating icon in the same spot instead
	const toggleItem = { button: ICON_VISIBILITY_OFF, font: iconFont, fontSize: 18, data: 'hudToggle', onClick: () => toggleHud(game) };

	const colorActive = [0, 0, 0.5];
	const colorAttention = [0.5, 0, 0];
	// phone layout (client/phone-layout.js): the TRICORDER (user 2026-10-16) — top bar with icons, a thin spine down the left;
	// a tap on the spine builds the desktop frame's left column (same blocks), a tap on a block or the column collapses it.
	const phone = isPhoneLayout();
	game.phoneLayout = phone;
	const loupeItem = { button: ICON_SEARCH, font: iconFont, fontSize: 18, data: 'search', onClick: () => { phoneSearchOpen = true; expandedBar = 'top'; updateLcarsHud(); } };
	// the open search closes with an X next to the eye (user 2026-10-16: "when you clicked the
	// loupe you cant go back to the previous hud state")
	const searchCloseItem = { button: 'X', background: colorAttention, color: [1, 1, 1], data: 'searchClose', onClick: () => { phoneSearchOpen = false; updateLcarsHud(); } };
	const sessionItems = user
		? (phone ? [
			{ button: ICON_MAIL, font: iconFont, fontSize: 18, data: 'messages', onClick: () => panels.showPanel('messages') },
			{ button: ICON_BELL, font: iconFont, fontSize: 18, data: 'notifications', background: colorAttention, color: [1, 1, 1], onClick: () => panels.showPanel('notifications') },
		] : [
			{ button: 'MESSAGES', onClick: () => panels.showPanel('messages') },
			{ button: `NOTIFICATIONS (${NOTIFICATION_COUNT})`, background: colorAttention, color: [1, 1, 1], onClick: () => panels.showPanel('notifications') },
		])
		: [{ button: 'LOGIN', background: colorActive, color: [1, 1, 1], onClick: () => panels.showPanel('login') }]; // overlay of main (user 2026-10-11)
	const log = (name) => () => console.log(`[lcars-hud] ${name}`);
	// "Jump to next colonizable planet" (old-bun-version intro footer; user 2026-10-11: bottom right):
	// each press goes one free EARTHLIKE planet further (providers/map.js nextColonizable)
	async function nextFreePlanet() {
		const r = await provider.request('game', 'map.js', 'nextColonizable', { skips: nextColonizableSkips });
		if (!r || r.$offline || !r.link) { nextColonizableSkips = 0; console.warn('[lcars-hud] next colonizable:', r); return; }
		nextColonizableSkips++;
		await navigation.loadPage(r.link);
		// this button is an explicit JUMP: page loads themselves never move the camera
		// (user 2026-10-10: "in SPA the link just always opens the dialogs")
		const m = r.link.match(/^\/(-?\d+)\/(-?\d+)\/(\d+)/);
		if (m) {
			await game.sectorMap.jumpTo({ x: Number(m[1]), y: Number(m[2]) });
			game.focus.focusPlanet({ x: m[1], y: m[2], index: m[3] });
		}
	}
	async function logout() {
		await provider.request('game', 'login.js', 'logout', {});
		await updateLcarsHud({ refreshUser: true });
		navigation.loadPage('/');
	}
	// the phone's expanded left/bottom bars: the desktop bars' buttons; the block of the mounted page is blue (desktop:
	// Planets, hard-wired), a tap on it closes the page; any button goes back to the default bar (top)
	const closeBar = async (then) => { await setBar('top'); then?.(); };
	const isActive = (href) => activePageKey === href.slice(1) + '-page';
	const pageBlock = (text, href) => ({
		button: text,
		...(isActive(href) ? { background: colorActive, color: [1, 1, 1] } : {}),
		onClick: () => closeBar(() => navigation.loadPage(isActive(href) ? '/' : href)),
	});
	const barBlock = (text, run) => ({ button: text, onClick: () => closeBar(run) });
	// the phone's bottom bar has no spacer item: the grey block between the two groups was the frame's own auto-fill bar,
	// which read as an empty button (ticket #23). NEXT FREE PLANET takes the room instead (sized after the first layout below).
	const nextItem = barBlock('NEXT FREE PLANET', nextFreePlanet);
	const phoneBars = phone ? {
		left: expandedBar === 'left' ? {
			top: [pageBlock('Planets', '/planets'), barBlock('Units', log('Units')), barBlock('Nation', log('Nation')), barBlock('Science', log('Science'))],
			bottom: [pageBlock('Settings', '/settings'), ...(user ? [barBlock('Logout', logout)] : [])],
		} : 12,
		bottom: expandedBar === 'bottom' ? {
			left: [barBlock('Map', () => navigation.loadPage('/')), pageBlock('Colonies', '/planets'), barBlock('Fleets', log('Fleets'))],
			right: [nextItem],
		} : 12,
	} : null;

	// globalThis.__guiViewport: VR lays the HUD out at a smaller virtual viewport
	// (miserit-anthar.js relayoutGuiFor rebuilds this frame on XR session start/end).
	// ONE layout for desktop and VR — no VR-specific sizes (user 2026-10-10: "side lanes");
	// if the bar does not fit, the viewport width is the only knob (XR_LAYOUT_WIDTH).
	const xy = currentXY();
	const searchItems = [
		{ label: 'X' },
		{ input: xy.x, size: 70, data: 'x', align: 'center', selectable, editable },
		{ label: 'Y' },
		{ input: xy.y, size: 70, data: 'y', align: 'center', selectable, editable },
		{ button: 'GO', onClick: () => go() },
	];
	const vp = globalThis.__guiViewport;
	const frameOpts = {
		width: ((vp && vp.width) || window.innerWidth || 1200) - 20,
		height: ((vp && vp.height) || window.innerHeight || 800) - 20,
		font,
		fontSize: 16,
		// desktop: white at 75 % (the map's hex lines shine faintly through, measured 2026-10-24). Phone (ticket #30): solid, the grey that the
		// 75 % white shows over the dark map (measured 188,190,198 over empty space; the box renders colours slightly darker, hence the factor), so nothing of the map shows through any bar
		color: phone ? [0.749, 0.757, 0.788, 1] : [1, 1, 1, .75],
		borders: {
			top: {
				// phone, search open: the X/Y/GO field starts at the bar's left content edge (user 2026-10-23: "the search needs to be left aligned when its being expanded")
				left: phone && phoneSearchOpen ? (expandedBar === 'top' ? searchItems : []) : hrefItems([ { label: 'miserit anthar', href: '/', font: brandFontResolved } ]),
				center: phone ? [] : searchItems,
				right: phone
					? (expandedBar !== 'top' ? [] : phoneSearchOpen ? [searchCloseItem, toggleItem] : [loupeItem, ...sessionItems, toggleItem])
					: [...sessionItems, toggleItem],
			},
			// phone: thin left spine / bottom bar, or the desktop bar with its buttons when expanded (phoneBars)
			...(phone ? phoneBars : {
				bottom: {
					left: hrefItems([
						{ button: 'Map', href: '/' },
						{ button: 'Colonies', href: '/planets', toggle: true },
						{ button: 'Fleets', onClick: log('Fleets') },
					]),
					right: [
						{ button: 'NEXT FREE PLANET', onClick: nextFreePlanet },
						...(DEV ? [
							// DEV: in-game screenshot (desktop + XR → tests/device/xr-shots/), FOV rings (client/fov-rings.js)
							{ button: 'Shot', onClick: () => game.captureScreenshot?.().catch(e => console.error('[screenshot]', e)) },
							{ button: 'Rings', onClick: () => game.fovRings?.toggle() },
						] : []),
					],
				},
				left: {
					top: hrefItems([
						{ button: 'Planets', background: colorActive, color: [1, 1, 1], href: '/planets', toggle: true },
						{ button: 'Units', onClick: log('Units') },
						{ button: 'Nation', onClick: log('Nation') },
						{ button: 'Science', onClick: log('Science') },
					]),
					bottom: hrefItems([
						{ button: 'Settings', href: '/settings', toggle: true },
						...(user ? [{ button: 'Logout', onClick: logout }] : []),
					]),
				},
			}),
		},
	};
	let main;
	let prevArea = null;
	if (existing) {
		main = existing;
		prevArea = { ...existing.contentArea };
		// every side is named: a side the new state does not have is removed
		existing.update({ ...frameOpts, borders: { top: null, right: null, bottom: null, left: null, ...frameOpts.borders } });
	} else main = new LcarsBox(frameOpts);
	// GUI draw regime: over the scene, no depth interaction with the map. Frame first, widget
	// backgrounds next, texts last: with ONE number for all of them three broke the tie by
	// camera distance, so in VR the labels landed before/behind the segment background depending
	// on head pose and blinked (user 2026-10-10: "positional, depending on the mood of the camera").
	const base = (game.gui.renderOrder || 10000) + 100;
	// (an updated frame: its content slot holds the panels/page with their own numbers — only the frame's own parts)
	// three's groupOrder is the renderOrder of the NEAREST Group ancestor: the LcarsBox is a Group with renderOrder 0, so its frame/fills/texts
	// had groupOrder 0 and sorted BEHIND game.labels (Group 1000) and the sun/reticle; the button fills were additionally opaque (opaque list
	// is drawn before every transparent mesh). The box takes the GUI layer's number (ticket #24, 2026-10-24); depthTest is off anyway.
	if (phone && expandedBar === 'bottom' && main.buttons?.Fleets && main.buttons['NEXT FREE PLANET'] && main.sides.bottom?._end?.[0]) {
		const fleets = main.buttons.Fleets.position.x + main.sides.bottom._start[main.sides.bottom._start.length - 1].len / 2;
		const nextLen = main.sides.bottom._end[0].len, nextLeft = main.buttons['NEXT FREE PLANET'].position.x - nextLen / 2;
		const fill = nextLeft - (fleets + main.gap);
		if (fill > 1) { // close the gap: the button grows to the left, the frame draws no fill bar there
			nextItem.size = nextLen + fill;
			main.update({ ...frameOpts, borders: { top: null, right: null, bottom: null, left: null, ...frameOpts.borders } });
		}
	}
	main.renderOrder = game.gui.renderOrder || 10000;
	if (main.content) main.content.renderOrder = main.renderOrder; // the page slot is a Group too (its page background was groupOrder 0 as well)
	for (const part of main.children) {
		if (part === main.content) continue;
		part.traverse((o) => {
			if (!o.material) return;
			o.renderOrder = o === main.frame ? base : (o.material.uniforms?.atlas ? base + 2 : base + 1);
			o.material.depthTest = false;
			if (!o.material.transparent) { o.material.transparent = true; o.material.needsUpdate = true; }
		});
	}
	// Content panels (hud-panels.js) are box-ui trees holding LcarsBox panels. box-ui numbers
	// its own meshes from the root's renderOrder on layout(), but gives an LcarsBox (a foreign
	// child, a Group) ONE number for all its direct meshes → frame and its texts tied → the
	// texts vanished (Pico 2026-10-10). The Group's number G is three's groupOrder for the
	// whole panel; inside it: frame G-0.9, fills G-0.8, frame texts G-0.7 (fractions: G-1 is
	// the box-ui wrapper's own number), content box tree from G.
	const applyOrder = (root) => {
		root.renderOrder = base + 10;
		root.updateRenderOrder(base + 10);
		root.traverse((o) => { if (o.material) o.material.depthTest = false; });
		root.traverse((o) => {
			if (!o.frame) return; // an LcarsBox
			const G = o.renderOrder;
			for (const c of o.children) {
				if (!c.material) continue;
				c.renderOrder = c === o.frame ? G - 0.9 : (c.material.uniforms?.atlas ? G - 0.7 : G - 0.8);
			}
			o.contentBox?.updateRenderOrder(G);
		});
	};
	// X/Y + GO or Enter → the URL becomes /x/y and the map jumps there (user 2026-10-10); the
	// home component's handler does the jump on SPA navigation, client.js on an SSR entry
	const go = () => {
		const x = parseInt(main.fields.x.opts.text, 10) || 0;
		const y = parseInt(main.fields.y.opts.text, 10) || 0;
		navigation.loadPage(`/${x}/${y}`);
		if (phone && phoneSearchOpen) { phoneSearchOpen = false; updateLcarsHud(); } // the loupe closes with the jump
	};
	for (const f of [main.fields?.x, main.fields?.y]) if (f?.editing) f.editing.onSubmit = go;
	let panels;
	let reusedPanels = false;
	const topInset = phone ? hintInset(main) : 0;
	if (existing) {
		// the frame stayed: the panels only follow when the content area moved (the phone's bars change its size)
		const a = main.contentArea;
		const moved = topInset !== (existing.__hintInset || 0) || !prevArea || a.x !== prevArea.x || a.y !== prevArea.y || a.w !== prevArea.w || a.h !== prevArea.h;
		if (moved) { game.hudPanels?.dispose(); panels = createHudPanels(main, { font, applyOrder, topInset }); } else panels = game.hudPanels;
		game.phoneSpine?.dispose(); // hit areas follow the bars
		game.phoneSpine = null;
		game.hudPanels = panels;
		reusedPanels = !moved;
	} else {
		panels = createHudPanels(main, { font, applyOrder, topInset });
		retireOld(); // swap: the old frame goes, the new one comes in the same step (no frame without a HUD)
		game.gui.add(main);
		game.lcarsHud = main;
		game.hudPanels = panels;
	}
	main.__hintInset = topInset;
	if (phone) game.phoneSpine = createPhoneSpine(main, base, applyOrder, iconFont);
	placeBuildBadge(main); // ticket #25: the DOM dev badge sits in the frame's free content area, above the bottom bar, never over a bar
	game.rebuildHudFull = rebuildLcarsHud; // the full rebuild (VR relayout path); tests compare it pixel for pixel with the partial update
	game.rebuildHud = () => updateLcarsHud({ refreshUser: true }); // login (hud-panels, sign-up): the frame's items change only
	if (!reusedPanels) panels.adopt(); // the routed page of the current URL (built by the renderer) into the page slot
	await initHudToggle(game); // the visibility icon outside the frame (re-positioned for this viewport)
	// phones show the frame on entry (user 2026-10-21): the first-entry hint plays at once (armHint)
	syncHudVisibility(game);
	if (phone) armHint();
	syncXyFields();
	console.log('[lcars-hud] ready', user ? 'user ' + user.name : 'no user');
	return main;
}
